import { useState } from 'react';
import { errorMessage } from '../../../lib/errorMessage';
import { Link } from 'react-router-dom';
import { ArrowLeftRight, ExternalLink, FileSignature, Mail, MessageCircle, Phone, Trash2, X } from 'lucide-react';

import { Drawer } from '../../../components/Drawer/Drawer';
import { EntityIdBadge } from '../../../components/EntityIdBadge';
import { useToast } from '../../../components/Toast';
import { cn } from '../../../lib/cn';
import { mailtoHref, telHref, whatsappHref } from '../../../lib/quickActions';
import { formatHebrewRelative } from '../../../lib/relativeTime';
import { QuoteWizard } from '../../quotes';
import { LostReasonPrompt } from '../LostReasonPrompt';
import { LeadForm } from './LeadForm';
import type { LeadInput, LeadRow, LeadSourceRow } from './leads.schema';
import {
  useLeadActivity,
  useMarkLeadAsLost,
  useSoftDeleteLead,
  useUpsertLead,
  type LeadActivityRow,
} from './leadsQueries';
import styles from './leads.module.css';

const FORM_ID = 'lead-form';

type TabKey = 'details' | 'activity' | 'notes';

const TABS: { id: TabKey; label: string }[] = [
  { id: 'details', label: 'פרטים' },
  { id: 'activity', label: 'פעילות' },
  { id: 'notes', label: 'הערות' },
];

const ACTION_LABELS: Record<string, string> = {
  'leads.insert': 'נוצר',
  'leads.update': 'עודכן',
  'leads.delete': 'נמחק',
};

function actionLabel(action: string): string {
  return ACTION_LABELS[action] ?? action;
}

interface LeadDrawerProps {
  open: boolean;
  lead: LeadRow | null;
  sources: LeadSourceRow[];
  orgId: string | null;
  userId: string | null;
  onClose: () => void;
  onRequestConvert?: (lead: LeadRow) => void;
}

function QuickActions({ phone, email }: { phone: string | null; email: string | null }) {
  const wa = whatsappHref(phone);
  const tel = telHref(phone);
  const mailto = mailtoHref(email);
  if (!wa && !tel && !mailto) return null;
  return (
    <div className={styles.quickActions}>
      {wa ? (
        <a
          href={wa}
          target="_blank"
          rel="noreferrer noopener"
          className={styles.quickActionBtn}
          aria-label="פתח WhatsApp"
          title="WhatsApp"
        >
          <MessageCircle size={14} aria-hidden="true" />
        </a>
      ) : null}
      {mailto ? (
        <a
          href={mailto}
          className={styles.quickActionBtn}
          aria-label="שלח אימייל"
          title="אימייל"
        >
          <Mail size={14} aria-hidden="true" />
        </a>
      ) : null}
      {tel ? (
        <a
          href={tel}
          className={styles.quickActionBtn}
          aria-label="התקשר"
          title="חיוג"
        >
          <Phone size={14} aria-hidden="true" />
        </a>
      ) : null}
    </div>
  );
}

function ActivityList({ rows, isLoading }: { rows: LeadActivityRow[]; isLoading: boolean }) {
  if (isLoading) return <p className={styles.notice}>טוען פעילות...</p>;
  if (rows.length === 0) return <p className={styles.notice}>אין פעילות עדיין.</p>;
  return (
    <ul className={styles.activityList}>
      {rows.map((row) => (
        <li key={row.id} className={styles.activityRow}>
          <span className={styles.activityLabel}>{actionLabel(row.action)}</span>
          <span className={styles.activityTime}>{formatHebrewRelative(row.created_at)}</span>
        </li>
      ))}
    </ul>
  );
}

export function LeadDrawer({
  open,
  lead,
  sources,
  orgId,
  userId,
  onClose,
  onRequestConvert,
}: LeadDrawerProps) {
  const toast = useToast();
  const upsertLead = useUpsertLead(orgId, userId);
  const softDeleteLead = useSoftDeleteLead(orgId);
  const markAsLost = useMarkLeadAsLost(orgId);
  const isEditing = Boolean(lead);
  const busy = upsertLead.isPending || softDeleteLead.isPending || markAsLost.isPending;
  const [tab, setTab] = useState<TabKey>('details');
  const [lostOpen, setLostOpen] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);

  function handleQuoteCreated() {
    setQuoteOpen(false);
    toast.success('הצעת המחיר נשמרה והליד סומן כ«הוצעה»');
    onClose();
  }

  const { data: activity = [], isLoading: activityLoading } = useLeadActivity(
    orgId,
    isEditing ? (lead?.id ?? null) : null,
  );

  async function handleSubmit(values: LeadInput) {
    try {
      await upsertLead.mutateAsync({ id: lead?.id ?? null, values });
      toast.success('הליד נשמר');
      onClose();
    } catch {
      toast.error('לא הצלחנו לשמור · נסה שוב');
    }
  }

  async function handleDelete() {
    if (!lead) return;
    if (!window.confirm(`למחוק את ${lead.full_name}?`)) return;

    try {
      await softDeleteLead.mutateAsync(lead.id);
      toast.success('הליד נמחק');
      onClose();
    } catch (err) {
      toast.error(`לא הצלחנו למחוק · ${errorMessage(err)}`);
    }
  }

  async function handleConfirmLost(reason: string) {
    if (!lead) return;
    try {
      await markAsLost.mutateAsync({ leadId: lead.id, reason, prevNotes: lead.notes });
      toast.success('הליד סומן כ-Lost');
      setLostOpen(false);
      onClose();
    } catch {
      toast.error('לא הצלחנו לעדכן · נסה שוב');
    }
  }

  return (
    <>
      <Drawer
        open={open}
        onClose={onClose}
        eyebrow={
          <>
            ליד
            {isEditing && lead ? <EntityIdBadge id={lead.id} /> : null}
          </>
        }
        title={isEditing ? lead?.full_name : 'ליד חדש'}
        ariaLabel={isEditing ? 'עריכת ליד' : 'ליד חדש'}
        footer={
          <>
            {isEditing ? (
              <button
                type="button"
                className={styles.btnDanger}
                disabled={busy}
                onClick={() => {
                  void handleDelete();
                }}
              >
                <Trash2 size={15} aria-hidden="true" />
                מחק
              </button>
            ) : null}
            {isEditing && lead && lead.status !== 'converted' && lead.status !== 'disqualified' ? (
              <button
                type="button"
                className={styles.btnSecondary}
                disabled={busy}
                onClick={() => {
                  setLostOpen(true);
                }}
              >
                <X size={15} aria-hidden="true" />
                סמן כ-Lost
              </button>
            ) : null}
            {isEditing && lead && lead.status !== 'converted' && lead.status !== 'disqualified' ? (
              <button
                type="button"
                className={styles.btnSecondary}
                disabled={busy}
                onClick={() => {
                  setQuoteOpen(true);
                }}
              >
                <FileSignature size={15} aria-hidden="true" />
                שלח הצעת מחיר
              </button>
            ) : null}
            {isEditing && lead && lead.status !== 'converted' && onRequestConvert ? (
              <button
                type="button"
                className={styles.btnSecondary}
                disabled={busy}
                onClick={() => {
                  onRequestConvert(lead);
                }}
              >
                <ArrowLeftRight size={15} aria-hidden="true" />
                המר ללקוח
              </button>
            ) : null}
            <button type="button" className={styles.btnSecondary} disabled={busy} onClick={onClose}>
              בטל
            </button>
            {tab === 'details' ? (
              <button type="submit" form={FORM_ID} className={styles.btnPrimary} disabled={busy}>
                שמור
              </button>
            ) : null}
          </>
        }
      >
        {isEditing && lead ? (
          <div className={styles.drawerHeader}>
            <QuickActions phone={lead.phone} email={lead.email} />
            <Link
              to={`/leads/${lead.id}`}
              className={styles.btnSecondary}
              onClick={onClose}
            >
              <ExternalLink size={14} aria-hidden="true" />
              פתח דף מלא
            </Link>
          </div>
        ) : null}

        {isEditing ? (
          <div className={styles.tabs} role="tablist" aria-label="פרטי ליד">
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={tab === t.id}
                className={cn(styles.tab, tab === t.id && styles.tabActive)}
                onClick={() => {
                  setTab(t.id);
                }}
              >
                {t.label}
              </button>
            ))}
          </div>
        ) : null}

        {(!isEditing || tab === 'details') && (
          <LeadForm
            lead={lead}
            sources={sources}
            formId={FORM_ID}
            disabled={busy}
            onSubmit={handleSubmit}
          />
        )}
        {isEditing && tab === 'activity' ? (
          <ActivityList rows={activity} isLoading={activityLoading} />
        ) : null}
        {isEditing && tab === 'notes' && lead ? (
          <div className={styles.notesPane}>
            {lead.notes ? (
              <pre className={styles.notesText}>{lead.notes}</pre>
            ) : (
              <p className={styles.notice}>אין הערות לליד זה. ערוך מתוך כרטיסיית «פרטים».</p>
            )}
          </div>
        ) : null}
      </Drawer>

      <LostReasonPrompt
        open={lostOpen}
        title="סמן ליד כ-Lost"
        entityName={lead?.full_name ?? null}
        onCancel={() => {
          setLostOpen(false);
        }}
        onConfirm={handleConfirmLost}
      />

      <QuoteWizard
        open={quoteOpen}
        orgId={orgId}
        userId={userId}
        preselectedLead={
          lead
            ? { id: lead.id, full_name: lead.full_name, company_name: lead.company_name }
            : null
        }
        onClose={() => {
          setQuoteOpen(false);
        }}
        onCreated={handleQuoteCreated}
      />
    </>
  );
}
